import Spinner from '@components/spinner/Spinner';
import { useFetchWorkspaces } from '@hooks/getQueries';
import { Box } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState } from 'react';
import useSession from '../hooks/useSession';

import { useLocation, useNavigate } from 'react-router-dom';
const WorkspaceContext = createContext();

const WorkspaceProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useSession();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState(false);
  const [activeWorkspace, setActiveWorkspace] = useState(null);

  const { data: workspaces, isPending } = useFetchWorkspaces({ isAuthenticated });

  // Handle workspace switch
  // When switching to a newly created workspace, pass the workspace object directly
  // to avoid the race condition where the workspace isn't in the list yet
  const handleSwitchWorkspace = (workspaceId, workspaceObject = null) => {
    localStorage.setItem('previousActiveWorkspaceId', activeWorkspaceId);
    setIsSwitchingWorkspace(true);
    setActiveWorkspaceId(workspaceId);
    localStorage.setItem('activeWorkspaceId', workspaceId);

    // Use provided workspace object (for new workspaces) or find from existing list
    const workspace =
      workspaceObject || workspaces?.workspaces?.find((ws) => ws.id === workspaceId);
    setActiveWorkspace(workspace);

    queryClient.invalidateQueries();
    // ---- UPDATE URL WITHOUT LOSING QUERY PARAMS ----
    const search = location.search; // keeps ?tab=members etc.
    navigate(`/workspaces/${workspaceId}${search}`, { replace: true });
    setTimeout(() => setIsSwitchingWorkspace(false), 2000);
  };

  // Set default workspace or switch based on URL
  useEffect(() => {
    if (isAuthenticated && workspaces?.workspaces?.length > 0) {
      // 1. Check if we're on a workspace detail page
      const match = location.pathname.match(/\/workspaces\/([a-zA-Z0-9_-]+)/);
      const urlWorkspaceId = match ? match[1] : null;

      // 2. Determine which ID to use
      const savedWorkspaceId = localStorage.getItem('activeWorkspaceId');

      let workspaceToSet = null;

      const isUrlWorkspaceAvailable = workspaces.workspaces.some((ws) => ws.id === urlWorkspaceId);

      // Priority: URL (only if available) > Saved > First available
      if (urlWorkspaceId && isUrlWorkspaceAvailable) {
        workspaceToSet = urlWorkspaceId;
      } else if (
        savedWorkspaceId &&
        workspaces.workspaces.some((ws) => ws.id === savedWorkspaceId)
      ) {
        workspaceToSet = savedWorkspaceId;
      } else {
        workspaceToSet = workspaces.workspaces[0]?.id;
      }

      // 3. Trigger switch if different from current state
      if (workspaceToSet && workspaceToSet !== activeWorkspaceId) {
        // If it's a transition from one active workspace to another, show switching UI
        if (activeWorkspaceId) {
          setIsSwitchingWorkspace(true);
          setTimeout(() => setIsSwitchingWorkspace(false), 2000);
        }

        const workspace = workspaces.workspaces.find((ws) => ws.id === workspaceToSet);
        if (workspace) {
          localStorage.setItem('activeWorkspaceId', workspace.id);
          setActiveWorkspace(workspace);
          setActiveWorkspaceId(workspace.id);
          queryClient.invalidateQueries();
        }
      }
    }
  }, [isAuthenticated, workspaces, location.pathname, activeWorkspaceId, queryClient]);

  if (isPending || isSwitchingWorkspace) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Spinner />
        {/* <Typography variant="h6">Loading workspaces...</Typography> */}
      </Box>
    );
  }

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces: workspaces?.workspaces,
        activeWorkspaceId,
        activeWorkspace,
        handleSwitchWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export { WorkspaceContext, WorkspaceProvider };

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};
